/**
 * Linked Excel Editor Agent
 *
 * Agent for editing Excel workbooks that are linked to PowerPoint slides.
 * Enables live data updates without breaking chart/table connections.
 *
 * ## Architecture
 *
 * This agent is called as an agent-as-tool from the Orchestrator. It:
 * 1. Creates a session with the Python backend (clones the file)
 * 2. Fetches workbook context and injects it as a reminder (NOT a tool)
 * 3. Exposes tools for reading and editing the workbook
 * 4. Tracks all changes including dependent cell updates
 * 5. Returns results via the Submit Results Pattern
 *
 * ## Key Features
 *
 * - **File cloning**: Never edits original files
 * - **Change tracking**: Tracks direct edits AND dependent cell updates
 * - **Safe inserts**: Row/column inserts preserve data table integrity
 * - **Two edit formats**: V1 (legacy) and V2 (modern) supported
 * - **Workbook context as reminder**: Auto-injected, pruned from old messages
 * - **User Interview**: Optional tool for gathering clarifications
 *
 * ## Usage
 *
 * ```typescript
 * const result = await linkedExcelEditorAgent.execute({
 *     targetFile: {
 *         path: "C:/Users/.../Financial_Model.xlsx",
 *         workbookId: "workbook_abc123",
 *         isOpen: true
 *     },
 *     requirements: [
 *         { id: "A", requirement: "Update Q4 revenue to $2.5M" }
 *     ],
 *     enableUserInterview: true // Optional
 * }, callbacks);
 * ```
 */

import { z } from "zod";
import { AgentBaseClass } from "../base/agentBaseClass";
import { ToolDescription } from "../base/toolDescription";
import { ToolBase } from "../base/toolBase";
import type { SubmitResultsParams } from "../base/submitResults.schema";
import {
  AgentResult,
  ResultItem,
  ResolvedContextItem,
  ThreadId,
  CancellationToken,
  AgentCallbacks,
} from "../../core/types";

import {
  LinkedExcelEditorParamsSchema,
  type LinkedExcelEditorParams,
  type LinkedExcelResultData,
  type TargetFile,
  type ArtifactInfo,
} from "./linkedExcelEditor.schema";

// Unified read tool (handles all read operations in one tool)
import { ReadExcelTool } from "./tools/readExcel";

// Shared factory tools from agent-runtime (execution via BackendSessionExcelRunner)
import {
  createEditContentTool,
  createEditFormatTool,
  createEditChartTool,
  createEditStructureTool,
  type EditContentTool,
  type EditFormatTool,
  type EditChartTool,
  type EditStructureTool,
} from "@carousel-office/agent-runtime";
import { BackendSessionExcelRunner } from "./runtime/backendSessionExcelRunner";
import { SubmitArtifactTool } from "./tools/submitArtifact";
import { LinkedExcelSubmitResultsTool } from "./tools/submitResults";
import { UserInterviewTool } from "../tools/userInterview";
import type { SubmitArtifactResponse } from "./tools/submitArtifact/submitArtifact.schema";
import { DEFAULT_EXCEL_API_BASE_URL } from "./constants";
import { fetchWithRetry } from "./utils/fetchWithRetry";
import { backendSettingsService, type BackendCustomInstructions } from "@carousel-office/shared";
import {
  buildToolsSection,
  getContextNoteSection,
  getEvaluationSection,
  getIntroSection,
  getSuggestionsSection,
} from "./prompt";

// ═══════════════════════════════════════════════════════════════════════════
// WORKBOOK CONTEXT TYPES (for reminder injection)
// ═══════════════════════════════════════════════════════════════════════════

interface SheetInfo {
  name: string;
  index: number;
  usedRange: string | null;
  rowCount: number;
  colCount: number;
  hasCharts: boolean;
  chartCount: number;
  isHidden: boolean;
}

interface WorkbookContext {
  workbookName: string;
  sessionId: string;
  sheetCount: number;
  sheets: SheetInfo[];
  asString: string;
  recurringString: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_SUBMIT_TOOLKIT_MODE: LinkedExcelSubmitToolkitMode = "override";

export type LinkedExcelSubmitToolkitMode = "legacy" | "override";

export interface LinkedExcelEditorAgentOptions {
  apiBaseUrl?: string;
  submitToolkitMode?: LinkedExcelSubmitToolkitMode;
}

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const LINKED_EXCEL_EDITOR_AGENT_TOOL_PROMPT = `Edit Excel workbooks linked to PowerPoint presentations.

Use this agent when you need to:
- Update values or formulas in Excel workbooks
- Add rows, columns, or sheets
- Modify data that feeds PowerPoint charts/tables
- Perform complex multi-step Excel edits

The agent works on a CLONE of the file (original is never modified).
All changes are tracked including dependent cell updates.`;

// ═══════════════════════════════════════════════════════════════════════════
// LINKED EXCEL EDITOR AGENT
// ═══════════════════════════════════════════════════════════════════════════

export class LinkedExcelEditorAgent extends AgentBaseClass<
  LinkedExcelEditorParams,
  AgentResult<LinkedExcelResultData>
> {
  // ═══════════════════════════════════════════════════════════════════════
  // AGENT-AS-TOOL PROMPT CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════

  /** System prompt description for agent-as-tool */
  public readonly systemPromptDescription = LINKED_EXCEL_EDITOR_AGENT_TOOL_PROMPT;

  // ═══════════════════════════════════════════════════════════════════════
  // REQUIRED IMPLEMENTATIONS
  // ═══════════════════════════════════════════════════════════════════════

  readonly agentType = "linkedExcelEditor";
  readonly name = "linkedExcelEditorAgent";
  readonly isAgent = true;

  // Tools this agent can use
  // Unified read tool (handles all read operations: content, formatting, charts, formulaTrace, tableAnalysis)
  private readExcelTool: ReadExcelTool;

  // Direct edit tools
  private editContentTool: EditContentTool;
  private editFormatTool: EditFormatTool;
  private editChartTool: EditChartTool;

  // Direct tools
  private editStructureTool: EditStructureTool; // Direct tool for structure operations
  private submitArtifactTool: SubmitArtifactTool; // Direct tool for exporting content to PowerPoint
  private linkedExcelSubmitResultsTool: LinkedExcelSubmitResultsTool; // Override submitResults tool with optional artifact export
  private userInterviewTool: UserInterviewTool;
  protected toolCalls: ToolBase[];
  private submitToolkitMode: LinkedExcelSubmitToolkitMode;

  // Schema for validation
  public paramsSchema =
    LinkedExcelEditorParamsSchema as unknown as z.ZodType<LinkedExcelEditorParams>;

  // ═══════════════════════════════════════════════════════════════════════
  // STREAMING FIELD CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════

  fieldsToCheck = [{ name: "targetFile", type: "object" as const, list_status: false, order: 1 }];

  listsToCheck = [
    {
      name: "requirements",
      type: "array" as const,
      item_type: "object" as const,
      list_status: true,
      order: 2,
      check_sub_field: "requirement",
    },
  ];

  // ═══════════════════════════════════════════════════════════════════════
  // INFRASTRUCTURE FLAGS
  // ═══════════════════════════════════════════════════════════════════════

  protected useThreadManagement = true;
  protected useTaskTracking = true;
  protected useContextResolution = true;
  protected useSubmitResultsPattern = true;
  protected stream = true;
  // maxResponseTokens: inherits 0 from base → adapter uses model maximum
  protected maxToolLoopIterations = 450;

  // ═══════════════════════════════════════════════════════════════════════
  // SESSION STATE
  // ═══════════════════════════════════════════════════════════════════════

  private sessionId: string | null = null;
  private clonePath: string | null = null;
  private targetFile: TargetFile | null = null;
  private apiBaseUrl: string;

  // Workbook context (fetched once, injected as reminder)
  private workbookContext: WorkbookContext | null = null;
  private sheetRenameHistory: Record<string, string[]> = {};
  private lastSheetOrder: string[] = [];
  private excelModeCustomInstructionsText = "";

  // Whether user interview is enabled for this execution
  private userInterviewEnabled: boolean = false;

  // Artifacts created during this session (for passing to orchestrator)
  private createdArtifacts: ArtifactInfo[] = [];

  // ═══════════════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════════════

  constructor(
    apiBaseUrlOrOptions: string | LinkedExcelEditorAgentOptions = DEFAULT_EXCEL_API_BASE_URL,
    options?: Omit<LinkedExcelEditorAgentOptions, "apiBaseUrl">
  ) {
    super();
    const config = this.resolveConstructorConfig(apiBaseUrlOrOptions, options);
    this.apiBaseUrl = config.apiBaseUrl;
    this.submitToolkitMode = config.submitToolkitMode;

    // Initialize unified read tool (handles all read operations)
    // Session mode uses session_id for the current workbook being edited
    this.readExcelTool = new ReadExcelTool({
      mode: "session",
      excelBackendUrl: this.apiBaseUrl,
    });

    // Initialize edit tools via shared agent-runtime factories + BackendSessionExcelRunner
    const runner = new BackendSessionExcelRunner(this.apiBaseUrl);
    this.editContentTool = createEditContentTool(runner) as any;
    this.editFormatTool = createEditFormatTool(runner) as any;
    this.editChartTool = createEditChartTool(runner) as any;
    this.editStructureTool = createEditStructureTool(runner) as any;
    this.submitArtifactTool = new SubmitArtifactTool(this.apiBaseUrl); // Direct tool for exporting to PowerPoint
    this.linkedExcelSubmitResultsTool = new LinkedExcelSubmitResultsTool(this.submitArtifactTool);
    this.userInterviewTool = new UserInterviewTool();

    // Base tools (userInterview added conditionally in beforeExecute)
    // ORDER: Read first, then edit tools, then structure/submit tools
    this.toolCalls = [
      // Unified read tool (handles content, formatting, charts, formulaTrace, tableAnalysis)
      this.readExcelTool,

      // Direct edit tools
      this.editContentTool,
      this.editFormatTool,
      this.editChartTool,

      // Direct tools
      this.editStructureTool, // Direct tool for structure operations
    ];

    if (this.submitToolkitMode === "legacy") {
      this.toolCalls.push(this.submitArtifactTool);
    } else {
      // Override submitResults mode:
      // include a linked-excel submitResults tool that can export artifacts inline.
      this.toolCalls.push(this.linkedExcelSubmitResultsTool);
    }

    this.description = new ToolDescription(
      this.name,
      this.systemPromptDescription,
      LinkedExcelEditorParamsSchema
    );

    // Initialize agent (injects submitResults tool)
    this.initializeAgent();
  }

  private resolveConstructorConfig(
    apiBaseUrlOrOptions: string | LinkedExcelEditorAgentOptions,
    options?: Omit<LinkedExcelEditorAgentOptions, "apiBaseUrl">
  ): Required<LinkedExcelEditorAgentOptions> {
    if (typeof apiBaseUrlOrOptions === "string") {
      return {
        apiBaseUrl: apiBaseUrlOrOptions,
        submitToolkitMode: options?.submitToolkitMode ?? DEFAULT_SUBMIT_TOOLKIT_MODE,
      };
    }

    return {
      apiBaseUrl: apiBaseUrlOrOptions.apiBaseUrl ?? DEFAULT_EXCEL_API_BASE_URL,
      submitToolkitMode: apiBaseUrlOrOptions.submitToolkitMode ?? DEFAULT_SUBMIT_TOOLKIT_MODE,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // REMINDER SYSTEM - Workbook Context Injection
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Override getReminderOptions to include workbook context
   *
   * This injects workbook context as a <non_user_automated_reminder> tag
   * which gets pruned from old messages automatically.
   */
  protected override getReminderOptions() {
    const baseOptions = super.getReminderOptions();

    // Linked Excel edits often depend on uploaded files and user editing preferences.
    // Keep external file reminders enabled, but inject excel-mode custom
    // instructions manually so they are host-agnostic.
    baseOptions.includeExternalFiles = true;
    baseOptions.includeCustomInstructions = false;

    // Add workbook context as external context
    const externalContext = baseOptions.externalContext || [];
    if (this.workbookContext) {
      externalContext.push({
        type: "other", // Using 'other' as workbook context doesn't fit predefined types
        title: `Workbook: ${this.workbookContext.workbookName}`,
        content: this.workbookContext.recurringString,
        source: "Excel Session",
      });
    }
    if (this.excelModeCustomInstructionsText) {
      externalContext.push({
        type: "other",
        title: "Custom Instructions (Excel Mode)",
        content: this.excelModeCustomInstructionsText,
        source: "Backend Settings",
      });
    }
    baseOptions.externalContext = externalContext;

    return baseOptions;
  }

  /**
   * Fetch workbook context from Python backend
   */
  private async fetchWorkbookContext(): Promise<WorkbookContext | null> {
    if (!this.sessionId) return null;

    try {
      console.log(
        `[LinkedExcelEditorAgent] Fetching workbook context for session ${this.sessionId}`
      );

      const response = await fetchWithRetry(`${this.apiBaseUrl}/excel/context`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: this.sessionId,
          include_style_analysis: true,
          include_empty_regions: true,
          include_off_limits: true,
          include_structure_analysis: true,
          include_chart_inventory: true,
          large_sheet_cutoff: 8000,
        }),
      });

      if (!response.ok) {
        console.warn(`[LinkedExcelEditorAgent] Failed to fetch context: ${response.status}`);
        return null;
      }

      const result = await response.json();

      // The backend returns { success, context: {...}, contextString }
      // Extract the context object which has camelCase keys
      const contextData = result.context || {};
      const sheetNames = this.collectSheetNamesFromContext(contextData);
      this.updateSheetRenameHistory(sheetNames);

      // Build the formatted string for LLM consumption
      const asString =
        typeof result.contextString === "string" && result.contextString.trim().length > 0
          ? result.contextString
          : this.formatWorkbookContextForReminder(contextData);
      const recurringString = this.formatRecurringWorkbookContext(contextData);

      console.log(
        `[LinkedExcelEditorAgent] Got workbook context: ${contextData.sheetCount || 0} sheets`
      );

      return {
        workbookName: contextData.workbookName || "Unknown",
        sessionId: this.sessionId,
        sheetCount: contextData.sheetCount || 0,
        sheets: contextData.sheets || [],
        asString,
        recurringString,
      };
    } catch (error) {
      console.warn(`[LinkedExcelEditorAgent] Error fetching workbook context:`, error);
      return null;
    }
  }

  private collectSheetNamesFromContext(context: any): string[] {
    const sheetNames: string[] = [];
    if (!Array.isArray(context?.sheets)) {
      return sheetNames;
    }
    for (const sheet of context.sheets) {
      if (typeof sheet === "string" && sheet.trim().length > 0) {
        sheetNames.push(sheet.trim());
        continue;
      }
      if (
        sheet &&
        typeof sheet === "object" &&
        typeof sheet.name === "string" &&
        sheet.name.trim().length > 0
      ) {
        sheetNames.push(sheet.name.trim());
      }
    }
    return sheetNames;
  }

  private updateSheetRenameHistory(currentSheetNames: string[]): void {
    const previous = this.lastSheetOrder;
    if (previous.length === currentSheetNames.length && previous.length > 0) {
      const previousSet = new Set(previous);
      const currentSet = new Set(currentSheetNames);
      const setsEqual =
        previousSet.size === currentSet.size &&
        [...previousSet].every((name) => currentSet.has(name));

      if (!setsEqual) {
        for (let idx = 0; idx < currentSheetNames.length; idx += 1) {
          const beforeName = previous[idx];
          const afterName = currentSheetNames[idx];
          if (!beforeName || !afterName || beforeName === afterName) continue;
          if (currentSet.has(beforeName)) continue;
          if (previousSet.has(afterName)) continue;

          const merged = [...(this.sheetRenameHistory[afterName] || [])];
          if (!merged.includes(beforeName)) merged.push(beforeName);
          for (const alias of this.sheetRenameHistory[beforeName] || []) {
            if (!merged.includes(alias)) merged.push(alias);
          }
          this.sheetRenameHistory[afterName] = merged;
          delete this.sheetRenameHistory[beforeName];
        }
      }
    }

    const pruned: Record<string, string[]> = {};
    for (const name of currentSheetNames) {
      const aliases = this.sheetRenameHistory[name] || [];
      if (aliases.length > 0) {
        pruned[name] = [...new Set(aliases)];
      }
    }
    this.sheetRenameHistory = pruned;
    this.lastSheetOrder = [...currentSheetNames];
  }

  /**
   * Format workbook context as a reminder string
   */
  private formatWorkbookContextForReminder(context: any): string {
    const lines: string[] = [
      "---- WORKBOOK CONTEXT ----",
      `Session ID: ${this.sessionId}`,
      `Total Sheets: ${context.sheetCount || 0}`,
    ];

    if (context.workbookName) {
      lines.push(`Filename: ${context.workbookName}`);
    }

    if (context.sheets && context.sheets.length > 0) {
      lines.push("");
      lines.push("Sheets:");

      for (let i = 0; i < context.sheets.length; i++) {
        const sheet = context.sheets[i];

        // Handle both object format and string format
        if (typeof sheet === "string") {
          // Backend returned just sheet names as strings
          lines.push(`  ${i + 1}. "${sheet}"`);
        } else {
          // Backend returned full sheet objects
          const sheetIndex = sheet.index ?? i;
          const sheetName = sheet.name || `Sheet${i + 1}`;
          const parts: string[] = [`  ${sheetIndex + 1}. "${sheetName}"`];

          // Handle dimensions (camelCase from backend)
          if (sheet.dimensions?.usedRange) {
            parts.push(`[${sheet.dimensions.usedRange}]`);
          }

          // Handle chart info (camelCase from backend)
          if (sheet.hasCharts) {
            const chartCount = sheet.chartCount || 0;
            parts.push(`(${chartCount} charts)`);
          }

          // Handle hidden sheets
          if (sheet.isHidden) {
            parts.push("[hidden]");
          }

          lines.push(parts.join(" "));
        }
      }
    }

    lines.push("");
    lines.push("Use readExcel tool to examine specific ranges or sheets.");

    return lines.join("\n");
  }

  private formatRecurringWorkbookContext(context: any): string {
    const lines: string[] = [
      "---- CURRENT WORKBOOK CONTEXT ----",
      `Session ID: ${this.sessionId}`,
      `Workbook: ${context.workbookName || "Unknown"}`,
      `Total Sheets: ${context.sheetCount || 0}`,
      "",
      "Sheets:",
    ];

    if (Array.isArray(context?.sheets) && context.sheets.length > 0) {
      for (let i = 0; i < context.sheets.length; i++) {
        const sheet = context.sheets[i];
        const sheetName =
          typeof sheet === "string"
            ? sheet
            : typeof sheet?.name === "string"
              ? sheet.name
              : `Sheet${i + 1}`;
        const aliases = this.sheetRenameHistory[sheetName] || [];
        const historyPart =
          aliases.length > 0 ? ` (was: ${aliases.map((value) => `"${value}"`).join(", ")})` : "";
        const hiddenPart =
          typeof sheet?.visibility === "string" && sheet.visibility.toLowerCase() !== "visible"
            ? " [hidden]"
            : "";
        lines.push(`  ${i + 1}. "${sheetName}"${historyPart}${hiddenPart}`);
      }
    } else {
      lines.push("  (No sheets found)");
    }

    lines.push("");
    lines.push("Use readExcel tool to inspect specific ranges before editing.");
    return lines.join("\n");
  }

  private formatActiveCustomInstructions(instructions: BackendCustomInstructions): string {
    const categoryOrder: Array<keyof BackendCustomInstructions> = [
      "carouselChat",
      "search",
      "research",
      "execution",
    ];
    const blocks: string[] = [];
    for (const category of categoryOrder) {
      const entries = Array.isArray(instructions[category]) ? instructions[category] : [];
      for (const entry of entries) {
        const title = typeof entry?.title === "string" ? entry.title.trim() : "";
        const content = typeof entry?.instruction === "string" ? entry.instruction.trim() : "";
        if (!entry?.on || !content) {
          continue;
        }
        const safeTitle = title || `${category} instruction`;
        blocks.push(
          `<custom_instruction category="${category}" title="${safeTitle}">\n${content}\n</custom_instruction>`
        );
      }
    }
    return blocks.join("\n\n");
  }

  private async refreshExcelModeCustomInstructions(): Promise<void> {
    try {
      await backendSettingsService.flushPendingWrites();
      const instructions = await backendSettingsService.getCustomInstructions("excel");
      this.excelModeCustomInstructionsText = this.formatActiveCustomInstructions(instructions);
    } catch (error) {
      const cached = backendSettingsService.getCachedCustomInstructions("excel");
      this.excelModeCustomInstructionsText = cached
        ? this.formatActiveCustomInstructions(cached)
        : "";
      console.warn(
        "[LinkedExcelEditorAgent] Falling back to cached excel-mode custom instructions:",
        error
      );
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SYSTEM PROMPT
  // ═══════════════════════════════════════════════════════════════════════

  generateSystemPrompt(resolvedContext?: ResolvedContextItem[]): string {
    let contextSection = "";
    if (resolvedContext && resolvedContext.length > 0) {
      const contextDescriptions = resolvedContext
        .map((item) => {
          const ids = Array.from(item.resolved.keys()).join(", ");
          return `- ${item.original.purpose} (IDs: ${ids})`;
        })
        .join("\n");
      contextSection = `\n\n## Available Context\n${contextDescriptions}`;
    }

    const fileInfo = this.targetFile
      ? `\n\n## Target File\n- **Path**: ${this.targetFile.path}\n- **Workbook ID**: ${this.targetFile.workbookId || "Not assigned"}\n- **Status**: ${this.targetFile.isOpen ? "Open in Excel" : "Closed"}`
      : "";

    const submitModeSection =
      this.submitToolkitMode === "legacy"
        ? `## Submit Mode
Legacy submit mode is active:
- You may call \`submitArtifact\` one or more times for chart/range exports.
- Call \`submitResults\` once at the end to finish the run.`
        : `## Submit Mode
Override submit mode is active:
- Use a single final \`submitResults\` call.
- Include \`artifactsToCreate\` in \`submitResults\` when exports are needed.`;

    return `${getIntroSection()}

**Workbook context is automatically provided** in <non_user_automated_reminder> tags.
Always consult that reminder before editing.

${buildToolsSection(this.toolCalls)}

## Execution Workflow
1. Review workbook reminder context and requirements.
2. Read the workbook first (\`readExcel\`) before any edits.
3. Apply direct edits with \`editContent\`, \`editFormat\`, \`editChart\`, and/or \`editStructure\`.
4. Verify with follow-up \`readExcel\` calls after edits.
5. Submit once all requirements are completed.

## Chart Safety Contract (Required)
- For chart edits, always run \`readExcel\` with chart discovery/read first.
- For chart XML edits, pass \`readId\` from the latest \`readExcel.readCharts\` response when available.
- After \`editChart\`, run \`readExcel.readCharts\` again to confirm the new XML/chart state.
- If the chart changed unexpectedly, re-read and retry on the latest canonical chartXml.

${submitModeSection}

${getSuggestionsSection()}

${getContextNoteSection()}

${getEvaluationSection()}${fileInfo}${contextSection}`;
  }

  protected override buildInitialMessages(
    params: LinkedExcelEditorParams,
    _resolvedContext?: ResolvedContextItem[]
  ) {
    const requirementsMessage = this.buildRequirementsMessage(params.requirements || []);
    const snapshotBlock = this.workbookContext?.asString
      ? `\n\n=== WORKBOOK SNAPSHOT (ONE-TIME) ===\n${this.workbookContext.asString}`
      : "";
    return [
      {
        role: "user" as const,
        content: `${requirementsMessage}${snapshotBlock}`,
      },
    ];
  }

  // ═══════════════════════════════════════════════════════════════════════
  // LIFECYCLE HOOKS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Ensure sessions are always closed after execution, including error paths.
   * Save clones only for non-error outcomes.
   */
  public override async execute(
    parameters: unknown,
    callbacks?: unknown
  ): Promise<AgentResult<LinkedExcelResultData>> {
    let shouldSaveSession = false;

    try {
      const result = await super.execute(parameters, callbacks);
      shouldSaveSession = result.status !== "error";
      return result;
    } finally {
      if (this.sessionId) {
        await this.closeSession(shouldSaveSession);
      }
    }
  }

  /**
   * Before execution: create session, fetch context, configure tools
   */
  protected override async beforeExecute(context: {
    params: LinkedExcelEditorParams;
    threadId: ThreadId;
    taskId: string;
    cancellationToken: CancellationToken;
    resolvedContext: ResolvedContextItem[];
    conversationId: string;
    documentId: string;
    callbacks?: AgentCallbacks;
  }): Promise<void> {
    const { params } = context;

    console.log(`[LinkedExcelEditorAgent] Starting execution for: ${params.targetFile.path}`);

    this.targetFile = params.targetFile;
    this.sheetRenameHistory = {};
    this.lastSheetOrder = [];

    // Configure user interview tool based on param
    this.userInterviewEnabled = params.enableUserInterview ?? false;
    if (this.userInterviewEnabled) {
      // Add userInterview to tool calls if not already present
      if (!this.toolCalls.includes(this.userInterviewTool)) {
        this.toolCalls.push(this.userInterviewTool);
        console.log(`[LinkedExcelEditorAgent] User Interview tool enabled`);
      }
    } else {
      // Remove userInterview from tool calls if present
      const idx = this.toolCalls.indexOf(this.userInterviewTool);
      if (idx !== -1) {
        this.toolCalls.splice(idx, 1);
      }
    }

    // Create session with Python backend
    // TODO: Read editMode from settings (GlobalStateManager.getExcelExportSettings().editMode)
    // When editMode === "original", we should NOT clone the file and edit the original directly.
    // Currently, we ALWAYS clone. The Python backend session/create endpoint already supports
    // an optional "edit_original" flag, but we need to:
    // 1. Pass editMode to the session/create request
    // 2. Update clonePath logic (will be same as original when editing original)
    // 3. Add UI warnings when editing original files
    // 4. Handle the case where the file is open in Excel (COM considerations)
    try {
      const response = await fetchWithRetry(`${this.apiBaseUrl}/excel/session/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          original_path: params.targetFile.path,
          is_open: params.targetFile.isOpen,
          workbook_id: params.targetFile.workbookId,
          // TODO: Add edit_original: editMode === "original" when implementing edit mode
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create session: ${response.status} - ${errorText}`);
      }

      const session = await response.json();

      this.sessionId = session.session_id;
      this.clonePath = session.clone_path;
      this.createdArtifacts = []; // Reset artifacts for new session

      console.log(`[LinkedExcelEditorAgent] Session created: ${this.sessionId}`);
      console.log(`[LinkedExcelEditorAgent] Working on clone: ${this.clonePath}`);

      // Set session ID on unified read tool
      this.readExcelTool.setSessionId(this.sessionId);

      // Set session ID on direct edit tools
      this.editContentTool.setSessionId(this.sessionId);
      this.editFormatTool.setSessionId(this.sessionId);
      this.editChartTool.setSessionId(this.sessionId);

      // Set session ID on direct tools
      this.editStructureTool.setSessionId(this.sessionId);
      this.submitArtifactTool.setSessionId(this.sessionId);
      this.linkedExcelSubmitResultsTool.setSessionId(this.sessionId);

      // Fetch workbook context (injected as reminder, not a tool)
      this.workbookContext = await this.fetchWorkbookContext();
      await this.refreshExcelModeCustomInstructions();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`[LinkedExcelEditorAgent] Session creation failed:`, error);
      throw new Error(`Failed to initialize Excel session: ${errorMsg}`);
    }
  }

  /**
   * Handle tool results — track changes, store images from _rawImages,
   * and refresh context if needed.
   */
  protected override async onToolResult(toolName: string, result: unknown): Promise<void> {
    const isMutatingTool =
      toolName === "editContent" ||
      toolName === "editFormat" ||
      toolName === "editChart" ||
      toolName === "editStructure";

    if (isMutatingTool) {
      console.log(`[LinkedExcelEditorAgent] Edit complete from ${toolName}:`, result);

      // Store _rawImages from shared agent-runtime tools into ContextResolver
      const resultObj =
        result && typeof result === "object" ? (result as Record<string, unknown>) : {};
      const rawImages = resultObj._rawImages as
        | Array<{ id: string; base64: string; mimeType: string }>
        | undefined;
      if (Array.isArray(rawImages) && rawImages.length > 0) {
        try {
          const { getContextResolver } = require("@carousel-office/agent-runtime");
          const resolver = getContextResolver();
          for (const img of rawImages) {
            if (img.id && img.base64) {
              resolver
                .storeImageAsync(img.id, img.base64, img.mimeType || "image/png")
                .catch((err: unknown) =>
                  console.warn(`[LinkedExcelEditorAgent] Failed to store image ${img.id}:`, err)
                );
            }
          }
          console.log(
            `[LinkedExcelEditorAgent] Stored ${rawImages.length} images from ${toolName}`
          );
        } catch (e) {
          console.warn(`[LinkedExcelEditorAgent] Could not store _rawImages:`, e);
        }
      }

      // Refresh workbook context so subsequent reminder injections include latest
      // sheet list, structure, and rename history.
      const refreshedContext = await this.fetchWorkbookContext();
      if (refreshedContext) {
        this.workbookContext = refreshedContext;
      }
    }

    // Track artifacts created via submitArtifact tool
    if (toolName === "submitArtifact") {
      const artifactResult = result as SubmitArtifactResponse;
      if (artifactResult.success && artifactResult.artifact_id) {
        const artifactInfo: ArtifactInfo = {
          artifactId: artifactResult.artifact_id,
          description: artifactResult.description,
          artifactType: artifactResult.artifact_type,
          sourceSheet: artifactResult.source.sheet,
          sourceRef: artifactResult.source.ref,
        };
        this.createdArtifacts.push(artifactInfo);
        console.log(
          `[LinkedExcelEditorAgent] Artifact created: ${artifactResult.artifact_id} (total: ${this.createdArtifacts.length})`
        );
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RESULT PROCESSING
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Process results from submitResults tool
   */
  protected override async processSubmittedResults(
    submittedResults: SubmitResultsParams,
    _params: LinkedExcelEditorParams,
    context: {
      params: LinkedExcelEditorParams;
      threadId: ThreadId;
      taskId: string;
      cancellationToken: CancellationToken;
      resolvedContext: ResolvedContextItem[];
      conversationId: string;
      documentId: string;
      callbacks?: AgentCallbacks;
    }
  ): Promise<ResultItem<LinkedExcelResultData>[]> {
    const results: ResultItem<LinkedExcelResultData>[] = [];

    for (const submitted of submittedResults.results) {
      context.cancellationToken.throwIfCancelled();

      const relevantResults = submitted.relevantResults || {};
      const requirementArtifacts = Array.isArray(
        (relevantResults as Record<string, unknown>).artifacts
      )
        ? ((relevantResults as Record<string, unknown>).artifacts as ArtifactInfo[])
        : [];

      for (const artifact of requirementArtifacts) {
        if (
          !this.createdArtifacts.some((existing) => existing.artifactId === artifact.artifactId)
        ) {
          this.createdArtifacts.push(artifact);
        }
      }

      const artifactsForRequirement =
        requirementArtifacts.length > 0
          ? requirementArtifacts
          : this.submitToolkitMode === "legacy" && this.createdArtifacts.length > 0
            ? [...this.createdArtifacts]
            : undefined;

      results.push({
        requirementId: submitted.requirementId,
        relevantResults: {
          clonePath: this.clonePath || "",
          sessionId: this.sessionId || "",
          changesSummary: (relevantResults.changesSummary as string) || "",
          cellsModified: (relevantResults.cellsModified as number) || 0,
          applyChanges: true,
          // Include requirement-specific artifacts if provided, otherwise fallback to session artifacts.
          artifacts: artifactsForRequirement,
        },
        rationale: submitted.rationale,
      });

      console.log(
        `[LinkedExcelEditorAgent] Requirement ${submitted.requirementId}: ${submitted.rationale}`
      );
      if (this.createdArtifacts.length > 0) {
        console.log(
          `[LinkedExcelEditorAgent] Passing ${this.createdArtifacts.length} artifact(s) to orchestrator:`,
          this.createdArtifacts.map((a) => a.artifactId).join(", ")
        );
      }
    }

    return results;
  }

  /**
   * Build final result
   */
  protected buildFinalResult(
    results: ResultItem<LinkedExcelResultData>[],
    threadId: ThreadId,
    tokensUsed?: number,
    summary?: string
  ): AgentResult<LinkedExcelResultData> {
    if (results.length === 0) {
      return this.buildPartialResult(results, threadId, tokensUsed, summary);
    }

    const result = this.buildResult(results, threadId, tokensUsed, summary);

    // Note: Session metadata (sessionId, clonePath, originalPath) is tracked
    // internally but not added to AgentResult as it's not part of the type contract

    return result;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Close the session (called externally when done)
   */
  public async closeSession(save: boolean = true): Promise<void> {
    if (!this.sessionId) return;

    try {
      const response = await fetchWithRetry(`${this.apiBaseUrl}/excel/session/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: this.sessionId,
          save,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to close session: ${response.status} - ${errorText}`);
      }

      console.log(`[LinkedExcelEditorAgent] Session closed (save=${save})`);
    } catch (error) {
      console.error(`[LinkedExcelEditorAgent] Error closing session:`, error);
    }

    this.sessionId = null;
    this.clonePath = null;
    this.workbookContext = null;
    this.sheetRenameHistory = {};
    this.lastSheetOrder = [];
    this.createdArtifacts = [];
  }
}

export default LinkedExcelEditorAgent;
